import type { RetailerKey } from '@vizit/constants';
import type { ScoreLevel } from '@vizit/organisms';
import { createContext } from 'react';
import type { ProductTableState } from './product-table-provider.state';

interface ProductTableContextType extends ProductTableState {
  /**
   * The ID of the organization to fetch products for
   *
   * @required
   * @type {string}
   */
  organizationId: string;
  /**
   * The ID of the portfolio to fetch products for
   *
   * @optional
   * @type {string}
   */
  portfolioId?: string;
  /**
   * Function to filter by brand
   *
   * @param brand - Brand name to filter by
   * @type {(brand: string) => void}
   */
  onFilterBrand: (brand: string) => void;
  /**
   * Function to filter by product category
   *
   * @param productCategoryId - Product category ID to filter by
   * @param productCategoryName - Product category name to filter by
   * @type {(productCategoryId: string, productCategoryName: string) => void}
   */
  onFilterProductCategory: (productCategoryId: string, productCategoryName: string) => void;
  /**
   * Function to filter by listing score level
   *
   * @param listingScoreLevel - Score level to filter by
   * @type {(listingScoreLevel: ScoreLevel) => void}
   */
  onFilterListingScoreLevel: (listingScoreLevel: ScoreLevel) => void;
  /**
   * Function to update the unified search term (searches name OR item_id)
   *
   * @param value - Search term
   * @type {(value: string) => void}
   */
  onSearchTermChange: (value: string) => void;
  /**
   * Function to filter by retailer
   *
   * @param retailer - RetailerKey to filter by, or undefined to clear
   * @type {(retailer: RetailerKey | undefined) => void}
   */
  onFilterRetailer: (retailer: RetailerKey | undefined) => void;
}

const ProductTableContext = createContext<ProductTableContextType | null>(null);

export { ProductTableContext };
export type { ProductTableContextType };
